package co.maytes.standupbot.dashboard.features.standupruns

import androidx.compose.runtime.Composable
import co.maytes.standupbot.dashboard.components.motion.pressable
import co.maytes.standupbot.dashboard.components.widgets.Card
import co.maytes.standupbot.dashboard.styles.Motion
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.dto.standup.StandupDto
import co.maytes.standupbot.shared.dto.standuprun.StandupRunStatusDto
import co.maytes.standupbot.shared.dto.standuprun.StandupRunSummaryDto
import co.maytes.standupbot.shared.util.RelativeTime
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.datetime.Clock
import kotlinx.datetime.Instant
import kotlinx.datetime.TimeZone
import org.jetbrains.compose.web.attributes.alt
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Text

enum class RunListDensity {
    Comfortable,
    Dense,
}

@Composable
fun StandupListItem(
    item: StandupRunSummaryDto,
    standup: StandupDto? = null,
    density: RunListDensity = RunListDensity.Comfortable,
    onClick: (String) -> Unit,
) {
    val now = Clock.System.now()
    val tz = standup?.let { runCatching { TimeZone.of(it.timezone) }.getOrNull() }
        ?: TimeZone.currentSystemDefault()
    val channel = standup?.slackChannelName?.takeIf { it.isNotBlank() } ?: standup?.slackChannelId
    val whenTime = item.postedAt ?: item.scheduledFor
    val absolute = RelativeTime.formatDateTime(whenTime, tz)
    val relative =
        if (item.status == StandupRunStatusDto.DRAFTED) {
            RunStatusRules.draftTimingLabel(item.scheduledFor, now)
        } else {
            RelativeTime.format(whenTime, now, tz)
        }
    val dense = density == RunListDensity.Dense

    Card(
        modifier = Modifier
            .fillMaxWidth()
            .margin(bottom = if (dense) Spacing.xs else Spacing.md)
            .cursor(Cursor.Pointer)
            .pressable()
            .gap(if (dense) Spacing.xxs else Spacing.sm)
            .styleModifier {
                property("padding", if (dense) "10px 16px" else "18px 20px")
                property("display", "flex")
                property("flex-direction", "column")
            }
            .onClick { onClick(item.id) },
        interactive = true,
    ) {
        HeaderRow(item, channel, absolute, relative, dense)
    }
}

@Composable
private fun HeaderRow(
    item: StandupRunSummaryDto,
    channel: String?,
    absolute: String,
    relative: String,
    dense: Boolean,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .gap(Spacing.md)
            .styleModifier { property("flex-wrap", "wrap") },
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween,
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier.styleModifier {
                property("gap", "10px")
                property("flex", "1 1 auto")
                property("min-width", "0")
            },
        ) {
            StandupStatusChip(status = item.status)
            ProviderCauseChip(item.cause)
            SpanText(
                text = item.standupName,
                modifier = Modifier
                    .fontSize(Typography.body.size)
                    .fontWeight(600)
                    .color(Palette.textPrimary)
                    .styleModifier {
                        property("overflow", "hidden")
                        property("text-overflow", "ellipsis")
                        property("white-space", "nowrap")
                        property("min-width", "0")
                    },
            )
            if (!channel.isNullOrBlank()) {
                ChannelChip(channel)
            }
        }
        MetaRow(item, absolute, relative, dense)
    }
}

@Composable
private fun ChannelChip(name: String) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        modifier = Modifier.styleModifier {
            property("gap", "6px")
            property("align-items", "center")
            property("background-color", "${Palette.surfaceSubtle}")
            property("border-radius", "999px")
            property("padding", "3px 9px")
            property("min-width", "0")
        },
    ) {
        Img(
            src = "/brand/slack.svg",
            attrs = Modifier
                .styleModifier {
                    property("width", "12px")
                    property("height", "12px")
                    property("flex-shrink", "0")
                }
                .toAttrs { alt("Slack") },
        )
        SpanText(
            text = "#$name",
            modifier = Modifier
                .fontSize(Typography.caption.size)
                .fontWeight(500)
                .color(Palette.textSecondary)
                .styleModifier {
                    property("overflow", "hidden")
                    property("text-overflow", "ellipsis")
                    property("white-space", "nowrap")
                    property("max-width", "240px")
                },
        )
    }
}

@Composable
private fun MetaRow(
    item: StandupRunSummaryDto,
    absolute: String,
    relative: String,
    dense: Boolean,
) {
    Row(
        modifier = Modifier.styleModifier {
            property("gap", if (dense) "12px" else "20px")
            property("flex-wrap", "wrap")
            property("align-items", "center")
            property("justify-content", "flex-end")
            property("flex", "0 1 auto")
            property("min-width", "0")
        },
    ) {
        TimeColumn(absolute = absolute, relative = relative, scheduledFor = item.scheduledFor, postedAt = item.postedAt, status = item.status, dense = dense)
        Divider()
        MetaStat(label = if (item.sectionsCount == 1) "contributor" else "contributors", value = item.sectionsCount.toString(), dense = dense)
        Divider()
        MetaStat(label = "cost", value = formatCents(item.aiCostCents), monospaced = true, dense = dense)
        item.slackMessageUrl?.takeIf { it.isNotBlank() }?.let { url ->
            Divider()
            OpenInSlackLink(url)
        }
    }
}

@Composable
private fun OpenInSlackLink(url: String) {
    A(
        href = url,
        attrs = Modifier
            .color(Palette.accent)
            .fontSize(Typography.caption.size)
            .fontWeight(600)
            .styleModifier { property("text-decoration", "none") }
            .onClick { it.stopPropagation() }
            .toAttrs {
                attr("target", "_blank")
                attr("rel", "noopener noreferrer")
            },
    ) { Text("Open in Slack →") }
}

@Composable
private fun TimeColumn(
    absolute: String,
    relative: String,
    scheduledFor: Instant,
    postedAt: Instant?,
    status: StandupRunStatusDto,
    dense: Boolean,
) {
    if (dense) {
        SpanText(
            text = absolute,
            modifier = Modifier
                .fontSize(Typography.bodySmall.size)
                .fontWeight(500)
                .color(Palette.textPrimary)
                .styleModifier { property("font-family", Typography.FONT_SANS); property("font-variant-numeric", "tabular-nums") },
        )
        return
    }
    Column(
        modifier = Modifier.styleModifier {
            property("gap", "2px")
            property("min-width", "0")
        },
    ) {
        SpanText(
            text = absolute,
            modifier = Modifier
                .fontSize(Typography.bodySmall.size)
                .fontWeight(500)
                .color(Palette.textPrimary)
                .styleModifier { property("font-family", Typography.FONT_SANS); property("font-variant-numeric", "tabular-nums") },
        )
        SpanText(
            text = punctualityLabel(scheduledFor, postedAt, status) ?: relative,
            modifier = Modifier
                .fontSize(Typography.caption.size)
                .color(Palette.textTertiary),
        )
    }
}

@Composable
private fun Divider() {
    Box(
        modifier = Modifier.styleModifier {
            property("width", "1px")
            property("height", "24px")
            property("background-color", "${Palette.borderSubtle}")
        },
    ) {}
}

@Composable
private fun MetaStat(label: String, value: String, monospaced: Boolean = false, dense: Boolean = false) {
    if (dense) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier.gap(Spacing.xxs),
        ) {
            SpanText(
                text = value,
                modifier = Modifier
                    .fontSize(Typography.bodySmall.size)
                    .fontWeight(600)
                    .color(Palette.textPrimary)
                    .let {
                        if (monospaced) it.styleModifier { property("font-family", Typography.FONT_SANS); property("font-variant-numeric", "tabular-nums") } else it
                    },
            )
            SpanText(
                text = label,
                modifier = Modifier
                    .fontSize(Typography.caption.size)
                    .color(Palette.textTertiary),
            )
        }
        return
    }
    Column {
        SpanText(
            text = value,
            modifier = Modifier
                .fontSize(Typography.bodySmall.size)
                .fontWeight(600)
                .color(Palette.textPrimary)
                .let {
                    if (monospaced) it.styleModifier { property("font-family", Typography.FONT_SANS); property("font-variant-numeric", "tabular-nums") } else it
                },
        )
        SpanText(
            text = label,
            modifier = Modifier
                .fontSize(Typography.caption.size)
                .color(Palette.textTertiary),
        )
    }
}

private fun punctualityLabel(scheduledFor: Instant, postedAt: Instant?, status: StandupRunStatusDto): String? {
    if (status != StandupRunStatusDto.POSTED || postedAt == null) return null
    val deltaSec = postedAt.epochSeconds - scheduledFor.epochSeconds
    val absSec = if (deltaSec < 0) -deltaSec else deltaSec
    return when {
        absSec < 120L -> "posted on time"
        absSec < 3600L -> "posted ${absSec / 60}m ${if (deltaSec > 0) "late" else "early"}"
        else -> "posted ${absSec / 3600}h ${if (deltaSec > 0) "late" else "early"}"
    }
}
